import React, { useState, useRef, useEffect } from "react";
import {
  DEFAULT_COMMENT_CHAR_LIMIT,
  DEFAULT_COMMENT_TEXT_AREA_HEIGHT_PX,
} from "../../Helpers/constants";
import {
  useCreateCommentMutation,
  useGetOneCommentMutation,
} from "../misc/mutations";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import BlockIcon from "@mui/icons-material/Block";
import { CommentResponse } from "../../Helpers/backendResponseInterfaces";

interface WriteCommentProps {
  comment: string;
  postId: number;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  setComments: React.Dispatch<React.SetStateAction<CommentResponse[]>>;
  setCommentCount: React.Dispatch<React.SetStateAction<number>>;
}

const WriteComment: React.FC<WriteCommentProps> = ({
  comment,
  postId,
  setComment,
  setComments,
  setCommentCount,
}) => {
  const [isCommentLimitExceeded, setIsCommentLimitExceeded] =
    useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const commentContent = e.target.value;
    setComment(commentContent);
    setIsCommentLimitExceeded(
      commentContent.length > DEFAULT_COMMENT_CHAR_LIMIT,
    );
  };

  const prependCommentMutation = useGetOneCommentMutation(setComments);
  const afterCreateComment = (commentId: number) => {
    setComment("");
    setCommentCount((prevCount) => prevCount + 1);
    prependCommentMutation.mutate({ commentId });
  };

  const createComment = useCreateCommentMutation(afterCreateComment);
  const handleCommentSubmit = () => {
    if (comment.trim() && !isCommentLimitExceeded) {
      createComment.mutate({ content: comment, postId: postId });
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.style.overflowY =
        textareaRef.current.scrollHeight > DEFAULT_COMMENT_TEXT_AREA_HEIGHT_PX
          ? "scroll"
          : "hidden";
    }
  }, [comment]);

  const handleKeyPress = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === "Enter" && !isCommentLimitExceeded && comment.trim()) {
      e.preventDefault(); // Prevents the default action of the Enter key
      handleCommentSubmit();
    }
  };

  return (
    <div className="px-2 w-full relative mt-2" onKeyDown={() => {}}>
      <textarea
        ref={textareaRef}
        value={comment}
        onKeyDown={handleKeyPress}
        onChange={handleCommentChange}
        className={`p-2 border rounded-lg w-full text-start resize-none focus:outline-none ${
          isCommentLimitExceeded ? "border-red-500" : "border-gray-300"
        } leading-5`} // Changed from overflow-y-auto to overflow-hidden
        placeholder="Share your thoughts..."
        rows={1}
        style={{ maxHeight: `${DEFAULT_COMMENT_TEXT_AREA_HEIGHT_PX}px` }} // Maximum height for 6 lines
      />
      {comment.trim() && (
        <button
          className={`absolute top-1 right-3 text-blue-500 hover:text-blue-700 transition-opacity duration-300 ${
            isCommentLimitExceeded ? "opacity-50" : "opacity-100"
          }`}
          onClick={handleCommentSubmit}
          disabled={isCommentLimitExceeded}
        >
          {isCommentLimitExceeded ? (
            <BlockIcon color="disabled" />
          ) : (
            <ArrowCircleUpIcon color="secondary" />
          )}
        </button>
      )}
      <div
        className={`absolute bottom-2 ${
          comment.trim() ? "right-10" : "right-4"
        } text-gray-500`}
      >
        {comment.length}/{DEFAULT_COMMENT_CHAR_LIMIT}
      </div>
    </div>
  );
};

export default WriteComment;
